import { z } from 'zod';
import { SourceOfFundsConfigurationZod } from './sourceOfFundsSchemas';
import { ComplianceApiErrorResponseZod } from 'src/api_openapi/v2/compliance/schemas';

export const ParamsTypeZOD = z.object({
    requestBody: SourceOfFundsConfigurationZod,
});
export type ParamsType = z.TypeOf<typeof ParamsTypeZOD>;

const checkResponse = <A>(code: number, decoder: z.ZodType<A>, data: unknown): A => {
    const decodeResult = decoder.safeParse(data);
    if (decodeResult.success) {
        return decodeResult.data;
    }
    console.error('error details', {
        errors: decodeResult.error.errors,
        data,
    });
    throw Error(`Response decoding error /compliance/v1/source-of-funds/config -> post -> ${code}`);
};

export const Response200ZOD = SourceOfFundsConfigurationZod;
export type Response200Type = z.TypeOf<typeof Response200ZOD>;

export const Response400ZOD = z.string();
export type Response400Type = z.TypeOf<typeof Response400ZOD>;

export const Response500ZOD = ComplianceApiErrorResponseZod;
export type Response500Type = z.TypeOf<typeof Response500ZOD>;

const ExtraHeadersZod = z.object({
    authorization: z.string(),
});
type ExtraHeadersType = z.TypeOf<typeof ExtraHeadersZod>;

export type ApiSetSourceOfFundsConfigResponseType =
    | {
          status: 200;
          body: Response200Type;
      }
    | {
          status: 400;
          body: Response400Type;
      }
    | {
          status: 500;
          body: Response500Type;
      };

const convertHeaders = (params: Record<string, string | null | undefined>): Record<string, string> => {
    const result: Record<string, string> = {};

    for (const [name, value] of Object.entries(params)) {
        if (value !== null && value !== undefined) {
            result[name] = value;
        }
    }

    return result;
};

export const apiSetSourceOfFundsConfigRequest = async (
    api_url: string,
    params: ParamsType,
    extraHeaders: ExtraHeadersType
): Promise<ApiSetSourceOfFundsConfigResponseType> => {
    const url = `${api_url}/compliance/v1/source-of-funds/config`;
    const headers = convertHeaders({
        'Content-Type': 'application/json; charset=utf-8',
        ...extraHeaders,
    });

    const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(params.requestBody),
        credentials: 'include',
    });

    const status = response.status;

    let json: unknown = null;

    try {
        const text = await response.text();
        if (text !== '') {
            try {
                json = JSON.parse(text);
            } catch {
                json = text;
            }
        }
    } catch (_err) {
        throw Error(`Http status ${status} - response body could not be read`);
    }

    if (status === 200) {
        return { status: 200, body: checkResponse(200, Response200ZOD, json) };
    }

    if (status === 400) {
        return { status: 400, body: checkResponse(400, Response400ZOD, json) };
    }

    if (status === 500) {
        return { status: 500, body: checkResponse(500, Response500ZOD, json) };
    }

    throw new Error(`apiSetSourceOfFundsConfigRequest - unhandled response ${status}`);
};
